import React from 'react';
import { func, object, array, string, bool } from 'prop-types';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { FormattedMessage } from '../../../../util/reactIntl';
import css from './AcceptedPaymentTypesAccordion.module.css';

export const DEFAULT_PAYMENT_TYPES = [
  { id: 'venmo', label: 'Venmo' },
  { id: 'wise', label: 'Wise' },
  { id: 'revolut', label: 'Revolut' },
  { id: 'paypal', label: 'PayPal' },
  { id: 'zelle', label: 'Zelle' },
  { id: 'baselane', label: 'Baselane' },
  { id: 'home_swap', label: 'Home swap' },
];

const isDefaultPaymentType = paymentTypeId =>
  DEFAULT_PAYMENT_TYPES.some(paymentType => paymentType.id === paymentTypeId);

const getPaymentTypesWithSelectedCustoms = (paymentTypes, selectedPaymentTypes) => {
  const customPaymentTypes = paymentTypes || [];
  const savedCustomPaymentTypeIds = customPaymentTypes.map(paymentType => paymentType.id);
  const selectedCustomPaymentTypes = (selectedPaymentTypes || [])
    .filter(
      paymentType =>
        paymentType &&
        !isDefaultPaymentType(paymentType) &&
        !savedCustomPaymentTypeIds.includes(paymentType)
    )
    .map(paymentType => ({
      id: paymentType,
      label: paymentType.startsWith('custom_') ? 'Custom payment' : paymentType,
    }));

  return [...customPaymentTypes, ...selectedCustomPaymentTypes];
};

const AcceptedPaymentTypesAccordion = props => {
  const {
    isExpanded,
    onToggle,
    paymentTypes,
    selectedPaymentTypes,
    onPaymentTypeSelect,
    onAddCustom,
    onEditCustom,
    onRemoveCustom,
    className,
    rootClassName,
  } = props;

  const classes = classNames(rootClassName || css.root, className);

  const allPaymentTypes = [
    ...DEFAULT_PAYMENT_TYPES,
    ...getPaymentTypesWithSelectedCustoms(paymentTypes, selectedPaymentTypes),
  ];
  const selectedCount = selectedPaymentTypes?.length || 0;

  return (
    <div className={classes}>
      <div className={css.header} onClick={onToggle}>
        <div className={css.headerLeft}>
          <h3 className={css.title}>
            <FormattedMessage id="EditListingPricingForm.AcceptedPaymentTypes" />
          </h3>
        </div>
        <div className={css.headerRight}>
          {selectedCount > 0 && (
            <span className={css.selectionCount}>
              {selectedCount} <FormattedMessage id="EditListingPricingForm.Selected" />
            </span>
          )}
          <button
            className={classNames(css.chevron, { [css.expanded]: isExpanded })}
            type="button"
            aria-label="Toggle payment types"
          >
            <svg width="18" height="10" viewBox="0 0 18 10" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M8.55307 8.13421L1.28852 0.87941C1.21799 0.80851 1.13396 0.75249 1.04139 0.71467C0.948824 0.67684 0.849607 0.65797 0.749618 0.65918C0.649628 0.6604 0.550898 0.68166 0.459276 0.72172C0.367653 0.76178 0.285004 0.81982 0.216217 0.8924C0.0752906 1.04086 -0.00219163 1.23842 0.0002345 1.44311C0.00266254 1.64779 0.0848063 1.84346 0.229214 1.98853L8.031 9.77949C8.101 9.84997 8.18435 9.90578 8.27618 9.94368C8.36801 9.98157 8.46647 10.0008 8.5658 10.0002C8.66514 9.99956 8.76336 9.97916 8.85472 9.94015C8.94608 9.90114 9.02874 9.84432 9.09788 9.77299L17.1076 1.57261C17.2494 1.42581 17.3286 1.2297 17.3286 1.02563C17.3286 0.82156 17.2494 0.62545 17.1076 0.47865C17.038 0.40692 16.9546 0.3499 16.8625 0.31096C16.7704 0.27202 16.6715 0.25195 16.5715 0.25195C16.4715 0.25195 16.3725 0.27202 16.2804 0.31096C16.1883 0.3499 16.105 0.40692 16.0353 0.47865L8.55307 8.13421Z" fill="black" />
            </svg>
          </button>
        </div>
      </div>

      {isExpanded && (
        <div className={css.content}>
          {/* Payment type pills */}
          <div className={css.pillsContainer}>
            {allPaymentTypes.map(paymentType => {
              const isSelected = selectedPaymentTypes?.includes(paymentType.id);
              const isCustomPaymentType = !isDefaultPaymentType(paymentType.id);
              return (
                <span
                  key={paymentType.id}
                  className={classNames(css.pill, { [css.selected]: isSelected })}
                  onClick={() => {
                    if (isCustomPaymentType && isSelected) {
                      onEditCustom(paymentType.id);
                    } else {
                      onPaymentTypeSelect(paymentType.id);
                    }
                  }}
                  role="button"
                  tabIndex={0}
                >
                  {paymentType.label}
                  {isCustomPaymentType && isSelected ? (
                    <button
                      type="button"
                      className={css.removeCustomButton}
                      aria-label="Remove custom payment type"
                      onClick={e => {
                        e.stopPropagation();
                        onRemoveCustom(paymentType.id);
                      }}
                    >
                      <svg width="10" height="10" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 4L4 12M4 4L12 12" stroke="#712739" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </button>
                  ) : null}
                </span>
              );
            })}
            <button
              type="button"
              className={css.addCustomButton}
              onClick={onAddCustom}
            >
              <span className={css.addCustomIcon}>
                <svg width="27" height="27" viewBox="0 0 27 27" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path fillRule="evenodd" clipRule="evenodd" d="M13.5 0C6.04432 0 0 6.04432 0 13.5C0 20.9557 6.04432 27 13.5 27C20.9557 27 27 20.9557 27 13.5C27 6.04432 20.9557 0 13.5 0ZM14.7273 18.4091C14.7273 18.7346 14.598 19.0467 14.3678 19.2769C14.1377 19.5071 13.8255 19.6364 13.5 19.6364C13.1745 19.6364 12.8623 19.5071 12.6322 19.2769C12.402 19.0467 12.2727 18.7346 12.2727 18.4091V14.7273H8.59091C8.26542 14.7273 7.95325 14.598 7.7231 14.3678C7.49294 14.1377 7.36364 13.8255 7.36364 13.5C7.36364 13.1745 7.49294 12.8623 7.7231 12.6322C7.95325 12.402 8.26542 12.2727 8.59091 12.2727H12.2727V8.59091C12.2727 8.26542 12.402 7.95326 12.6322 7.7231C12.8623 7.49294 13.1745 7.36364 13.5 7.36364C13.8255 7.36364 14.1377 7.49294 14.3678 7.7231C14.598 7.95326 14.7273 8.26542 14.7273 8.59091V12.2727H18.4091C18.7346 12.2727 19.0467 12.402 19.2769 12.6322C19.5071 12.8623 19.6364 13.1745 19.6364 13.5C19.6364 13.8255 19.5071 14.1377 19.2769 14.3678C19.0467 14.598 18.7346 14.7273 18.4091 14.7273H14.7273V18.4091Z" fill="#712739" />
                </svg>
              </span>
              Add custom
            </button>
          </div>

          {/* Informational copy */}
          <div className={css.infoSection}>
            <p className={css.infoItem}>
              <strong>Baselane:</strong>
              {' '}
              <FormattedMessage id="EditListingPricingForm.BaselaneInfo" />
              {' '}
              <a href="https://get.baselane.com/lpc/banking-partner?atp=gfhr" target="_blank" rel="noopener noreferrer" className={css.link}>
                <FormattedMessage id="EditListingPricingForm.LearnMoreHere" />
              </a>
            </p>
            <p className={css.infoItem}>
              <strong>Home swap:</strong>
              {' '}
              <FormattedMessage id="EditListingPricingForm.HomeSwapInfo" />
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

AcceptedPaymentTypesAccordion.defaultProps = {
  isExpanded: false,
  paymentTypes: [],
  selectedPaymentTypes: [],
  className: null,
  rootClassName: null,
};

AcceptedPaymentTypesAccordion.propTypes = {
  isExpanded: PropTypes.bool,
  onToggle: func.isRequired,
  paymentTypes: array,
  selectedPaymentTypes: array,
  onPaymentTypeSelect: func.isRequired,
  onAddCustom: func.isRequired,
  onEditCustom: func.isRequired,
  onRemoveCustom: func.isRequired,
  className: string,
  rootClassName: string,
};

export default AcceptedPaymentTypesAccordion;
